import axios from "axios";
import { toast } from "sonner";
import { getTurnstileToken, needsTurnstile } from "./turnstile";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

/**
 * Préfixe avec BACKEND_URL si l'URL est relative et pointe vers /api/.
 * Utilisé pour les <img src> des assets servis par le backend.
 *   "/api/content/asset/foo.png" → "http://localhost:8000/api/content/asset/foo.png"
 *   "https://cdn..." → inchangé (URL absolue)
 *   "/img/logo.png"  → inchangé (asset statique du frontend)
 */
export function assetUrl(path) {
    if (!path) return path;
    if (/^https?:\/\//i.test(path)) return path;
    if (path.startsWith("/api/")) return `${BACKEND_URL}${path}`;
    return path;
}

const api = axios.create({
    baseURL: API,
    timeout: 60000,
});

api.interceptors.request.use((config) => {
    const token = localStorage.getItem("em_token");
    if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Anti-robot : jeton Cloudflare Turnstile sur les formulaires publics (cf. lib/turnstile.js).
// Si Turnstile échoue côté navigateur, on envoie quand même : c'est le serveur qui tranche.
api.interceptors.request.use(async (config) => {
    if (!needsTurnstile(config)) return config;
    try {
        const token = await getTurnstileToken();
        config.headers = config.headers || {};
        config.headers["X-Turnstile-Token"] = token;
    } catch (e) {
        console.warn("[turnstile]", e && e.message);
    }
    return config;
});

// RBAC (cf. rbac_spec.md §4/§6) : un module/endpoint refusé renvoie 403
// {"detail": "permission_denied", "permission": "<clé>"}. On avertit l'admin
// sans jamais le déconnecter (401 reste le seul cas de déconnexion, géré par
// AuthProvider.fetchMe côté lib/auth.jsx).
api.interceptors.response.use(
    (response) => response,
    (error) => {
        const status = error?.response?.status;
        const detail = error?.response?.data?.detail;
        if (status === 403 && detail === "permission_denied") {
            toast.error("Accès non autorisé");
        }
        return Promise.reject(error);
    }
);

export function formatApiErrorDetail(detail) {
    if (detail == null) return "Une erreur est survenue. Veuillez réessayer.";
    if (typeof detail === "string") return detail === "permission_denied" ? "Accès non autorisé" : detail;
    if (Array.isArray(detail))
        return detail
            .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
            .filter(Boolean)
            .join(" ");
    if (detail && typeof detail.msg === "string") return detail.msg;
    return String(detail);
}

export default api;
